import { CLINIC_TIMEZONE, zonedDateTime } from '@/lib/reporting/timezone';
import {
  formatCourseClock,
  formatCourseDateParts,
  type CourseDateParts,
} from '@/lib/courseDateFormat';

const DA_MONTHS = [
  'jan.',
  'feb.',
  'mar.',
  'apr.',
  'maj',
  'jun.',
  'jul.',
  'aug.',
  'sep.',
  'okt.',
  'nov.',
  'dec.',
] as const;
const EN_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

function weekdayFromCivil(year: number, month: number, day: number): number {
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).getUTCDay();
}

export function parseClinicZonedInstant(iso: string | null | undefined): CourseDateParts | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const zoned = zonedDateTime(date, CLINIC_TIMEZONE);
  return {
    date,
    weekday: weekdayFromCivil(zoned.year, zoned.month, zoned.day),
    day: zoned.day,
    month: zoned.month - 1,
    year: zoned.year,
    hours: zoned.hour,
    minutes: zoned.minute,
  };
}

function monthName(monthIndex: number, language: string): string {
  return language.toLowerCase().startsWith('en') ? EN_MONTHS[monthIndex] : DA_MONTHS[monthIndex];
}

/** "8. jun. 2026" */
export function formatClinicDateOnly(iso: string | null | undefined, language: string): string {
  const parts = parseClinicZonedInstant(iso);
  if (!parts) return '';
  const month = monthName(parts.month, language);
  if (language.toLowerCase().startsWith('en')) {
    return `${parts.day} ${month} ${parts.year}`;
  }
  return `${parts.day}. ${month} ${parts.year}`;
}

export function formatClinicCivilDateOnly(civil: string | null | undefined, language: string): string {
  if (!civil) return '';
  return formatClinicDateOnly(`${civil}T12:00:00+01:00`, language);
}

/** "fre. 6. nov. 2026" */
export function formatClinicWeekdayDate(iso: string | null | undefined, language: string): string {
  const parts = parseClinicZonedInstant(iso);
  if (!parts) return '';
  return formatCourseDateParts(parts, language);
}

export function formatClinicCivilWeekdayDate(civil: string | null | undefined, language: string): string {
  if (!civil) return '';
  return formatClinicWeekdayDate(`${civil}T12:00:00+01:00`, language);
}

/** "man. 2. nov. 2026 · 09.00" */
export function formatClinicDateTime(iso: string | null | undefined, language: string): string {
  const parts = parseClinicZonedInstant(iso);
  if (!parts) return '';
  return `${formatCourseDateParts(parts, language)} · ${formatCourseClock(parts.hours, parts.minutes)}`;
}

export function formatClinicMonthTitle(year: number, monthIndex: number, language: string): string {
  const namesDa = [
    'januar',
    'februar',
    'marts',
    'april',
    'maj',
    'juni',
    'juli',
    'august',
    'september',
    'oktober',
    'november',
    'december',
  ];
  const namesEn = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const name = language.toLowerCase().startsWith('en') ? namesEn[monthIndex] : namesDa[monthIndex];
  return `${name} ${year}`;
}

export function civilFromParts(year: number, monthIndex: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function addCivilDays(civil: string, days: number): string {
  const [year, month, day] = civil.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + days, 12, 0, 0));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

export function todayCivil(): string {
  const zoned = zonedDateTime(new Date(), CLINIC_TIMEZONE);
  return civilFromParts(zoned.year, zoned.month - 1, zoned.day);
}
